import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import type { GridItem } from '@/features/dashboard/lib/grid-math'
import type { ComponentKey } from '@/features/dashboard/lib/widget-sizes'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'

export { widgetActionAtom, type WidgetAction } from '@/state/widget-actions'
export type { GridItem }

// Widget instance with unique ID
export interface WidgetInstance {
  id: string // Unique persistent ID: 'watchlist-1', 'price-chart-2'
  componentKey: ComponentKey // Component type: 'watchlist', 'price-chart'
}

// Layout configuration per pathname
export interface PathLayoutConfig {
  activeComponents: WidgetInstance[] // Changed from ComponentKey[]
  layouts: {
    studio: GridItem[]
  }
  instanceCounters: Partial<Record<ComponentKey, number>> // Track next ID per component type
}

// Helper functions for instance ID generation
export function generateInstanceId(componentKey: ComponentKey, counter: number): string {
  return `${componentKey}-${counter}`
}

const SHORT_ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789'

function generateShortId(length = 5): string {
  let result = ''
  for (let i = 0; i < length; i++) {
    result += SHORT_ID_CHARS[Math.floor(Math.random() * SHORT_ID_CHARS.length)]
  }
  return result
}

// Generates instance IDs for newly created widgets using a short random suffix
// (e.g. 'tab-grouped-chart-x7k2p') instead of an incrementing counter.
//
// Old numeric-suffix IDs (e.g. 'tab-grouped-chart-1') keep working as-is for
// existing widgets - this only affects IDs created from now on. The old
// counter-based scheme could regenerate the same ID after a widget was
// deleted (instanceCounters is recomputed from the max suffix still present
// in the layout), causing stale per-card state - linking color, column
// visibility, etc. - to leak onto the new widget. Random suffixes make that
// collision effectively impossible.
export function generateNewInstanceId(
  componentKey: ComponentKey,
  existingIds: Iterable<string> = []
): string {
  const taken = new Set(existingIds)
  let id = `${componentKey}-${generateShortId()}`
  while (taken.has(id)) {
    id = `${componentKey}-${generateShortId()}`
  }
  return id
}

// Convert old layouts to new format (backward compatibility)
export function migrateLayoutToInstances(oldLayout: PathLayoutConfig): PathLayoutConfig {
  // If already migrated, return as-is
  if (
    oldLayout.activeComponents.length > 0 &&
    typeof oldLayout.activeComponents[0] === 'object' &&
    'id' in oldLayout.activeComponents[0]
  ) {
    return oldLayout
  }

  // Convert ComponentKey[] to WidgetInstance[]
  const counters: Record<string, number> = {}
  const newActiveComponents: WidgetInstance[] = (oldLayout.activeComponents as any[]).map((key) => {
    const componentKey = key as ComponentKey
    counters[componentKey] = (counters[componentKey] ?? 0) + 1
    return {
      id: generateInstanceId(componentKey, counters[componentKey]),
      componentKey: componentKey,
    }
  })

  // Update layout items to use instance IDs
  const newLayouts = { ...oldLayout.layouts }
  newLayouts.studio = newLayouts.studio.map((item, index) => ({
    ...item,
    i: newActiveComponents[index]?.id ?? item.i,
  }))

  return {
    activeComponents: newActiveComponents,
    layouts: newLayouts,
    instanceCounters: counters,
  }
}

// Storage for all pathname layouts
export type DashboardLayoutsState = Record<string, PathLayoutConfig>

// Persisted atom - saves to localStorage on every update
export const dashboardLayoutsAtom = atomWithStorage<DashboardLayoutsState>(
  STORAGE_KEYS.dashboard.layouts,
  {},
  safeStorage,
  { getOnInit: true }
)

// Tracks the currently active page (shared between PageTabs and useLayoutSync)
export const activePageIdAtom = atomWithStorage<string | null>(
  STORAGE_KEYS.dashboard.focusedPageId,
  null,
  safeStorage,
  { getOnInit: true }
)

export const pendingDashboardPageNavigationAtom = atom<string | null>(null)

/** Grid-unit position for the "add widget" hover ghost. null = hidden. */
export const addWidgetGhostAtom = atom<{ x: number; y: number; w: number; h: number } | null>(null)

// Whether the Add Components sheet (add-components-button.tsx) is open, and
// which width it's currently rendering at — read by dashboard-layout.tsx to
// reserve matching horizontal-scroll room, same pattern as the order form
// popup's ORDER_FORM_SHEET_WIDTH/orderFormPopupExpandedAtom.
export const addComponentsSheetOpenAtom = atom(false)
export const addComponentsSheetViewModeAtom = atom<'expanded' | 'list'>('expanded')
/** Which top-level tab the Workspace panel (same sheet) shows. */
export type WorkspacePanelTab = 'widgets' | 'templates'
export const workspacePanelTabAtom = atom<WorkspacePanelTab>('widgets')

/**
 * The widget most recently added through the Workspace panel / command palette.
 * dashboard-layout.tsx scrolls it into view and flashes a brief outline so an add
 * that lands below the fold is never silent. `at` makes re-adding distinct.
 */
export const recentlyAddedWidgetAtom = atom<{ id: string; at: number } | null>(null)

// Helper to get default layout for a pathname
export function getDefaultLayoutForPath(_pathname: string): PathLayoutConfig {
  return {
    activeComponents: [],
    layouts: {
      studio: [],
    },
    instanceCounters: {},
  }
}
